import { useNavigate, useLocation } from "react-router-dom";

/**
 * useAppNavigation
 *
 * Most screens in this app were written for React Navigation and expect a
 * `navigation` object (via the `navigation` prop or `useNavigation()`) exposing
 * `navigate(name)` / `goBack()`. But the screens are actually mounted by
 * react-router (`<Route element={<Screen/>}>`), so that `navigation` prop is
 * `undefined` -> `navigation.goBack()` throws "Cannot read properties of
 * undefined (reading 'goBack')" and `navigation.navigate(...)` silently fails.
 *
 * This hook returns a small object with the same shape, backed by react-router,
 * so existing screen code keeps working with a one-line change:
 *
 *   const Screen = () => {                  const Screen = () => {
 *     // was: ({ navigation })       ->       const navigation = useAppNavigation();
 *     ...                                     ...
 *
 * Screen names map to routes by prefixing "/" (e.g. "Notifi" -> "/Notifi").
 * Placeholder targets ("#", "", undefined) are ignored so dead buttons no-op
 * instead of navigating to a blank page.
 */
const DEFAULT_FALLBACK = "/DashboardBottom";

export default function useAppNavigation(fallback = DEFAULT_FALLBACK) {
  const navigate = useNavigate();
  const location = useLocation();

  const toPath = (name) => {
    if (typeof name !== "string") return null;
    const trimmed = name.trim();
    if (!trimmed || trimmed === "#") return null;
    return trimmed.startsWith("/") ? trimmed : `/${trimmed}`;
  };

  const go = (name, params, options = {}) => {
    const path = toPath(name);
    if (!path) return; // ignore placeholder / dead targets
    navigate(path, { state: params, ...options });
  };

  const canGoBack = () =>
    typeof window !== "undefined" && window.history.length > 1;

  const goBack = () => {
    if (canGoBack()) {
      navigate(-1);
    } else {
      navigate(fallback);
    }
  };

  return {
    navigate: go,
    push: go,
    replace: (name, params) => go(name, params, { replace: true }),
    goBack,
    canGoBack,
    // route params passed via navigate(name, params) are read from location.state
    params: location.state || {},
    // no-op stubs so React Navigation API calls don't crash
    setOptions: () => {},
    setParams: () => {},
    reset: () => {},
    dispatch: () => {},
    addListener: () => () => {},
    removeListener: () => {},
    isFocused: () => true,
  };
}
